import React, { useMemo, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import cx from "classnames";
import { Card, Select } from 'tea-component';
import RadarChart from '@/pages/dashboard/components/Chart/components/RadarChart';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { getLanguage } from '@home/utils/common';
import { getBasePath } from '@/utils/utils';
import { metricValueFormatter } from '@/pages/benchmark/utils/utils';
import './index.scss';

/**
 * 计算给定数据数组中的最大指标值。
 * 
 * @param dataArray - 包含多个对象的数组，每个对象代表一个指标及其值。
 * @returns 返回一个对象，其中包含每个指标的最大值。
 */
function calculateMaxMetrics(dataArray: any[]) {
  const maxMetrics: any = {};
  dataArray?.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (!maxMetrics[key] || parseFloat(item?.[key]?.value || 0) > parseFloat(maxMetrics?.[key]?.value || 0)) {
        maxMetrics[key] = item[key];
      }
    });
  });
  return maxMetrics;
}

const RadarChartCard = (props: any) => {
  const {
    className = '',
    style,
    height = 400,
    data,
    checkList,
    activeList,
    metricConfig,
    isEN,
    showCheck,
    dataFrame = 'pc',
    colorArr = ['#F17C86', '#6E7794', '#F8D74D', '#7F6FF1', '#5EC8F1', '#AD53D4', '#F19E55', '#488E89', '#B8BED1', '#45AF6A', '#9AA5C9', '#BE9737', '#9C8CEE', '#3EAFD7', '#E171BD', '#CE8038', '#6BB1AF', '#E5727C', '#ABD2FF', '#78E198', '#CDD8FD', '#F3C866', '#D4C0FF', '#7BE2FF', '#FFA7F3', '#FBB167', '#9EE5E2', '#FFAAB3', '#D4E8FF', '#92FBB0', '#E5EBFD', '#FFE683', '#F1DCFF', '#99FEFF', '#FFCAFF', '#FFD286', '#B9FFFE', '#FFCAD2']
  } = props;
  const { radarChartData, radarChartMaxData } = data || {};
  const [activeType, setActiveType] = useState<string>('percent');
  const isPercent = activeType === 'percent';
  const options = [
    {
      value: "percent",
      text: getLanguage(isEN, "Percentile Ranking", "百分位排序"),
    },
    {
      value: "number",
      text: getLanguage(isEN, "Numeric Ranking", "数值排序"),
    },
  ];
  const { loading, error, isNodata, maxMetricsList, chartDataList, currentGameId } = useMemo(() => {
    // 本游戏和竞品游戏的数据
    const { data: chartData, loading: chartLoading, errorMsg: chartError } = radarChartData || {};
    // 所有游戏指标最大值
    const { data: maxData, loading: maxLoading, errorMsg: maxError } = radarChartMaxData || {};
    // 无数据
    const isNodata = !chartData || !maxData;
    // 指标最大值
    const maxMetrics = maxData?.max_metrics || {};
    // 本游戏指标数据
    const gameMetrics = chartData?.dashboard_game_metrics || {};
    // 本游戏指标id
    const currentGameId = gameMetrics?.game_id || "";
    // 竞品游戏指标数据
    const compareGameMetrics = chartData?.compare_game_metrics_list || [];
    // top10%和medain指标数据
    const compareMetrics = chartData?.compare_metrics || {};
    const top10perMetrics: any = { game_name: checkList?.[0]?.name, game_id: checkList?.[0]?.value, metrics: {} };
    const medianMetrics: any = { game_name: checkList?.[1]?.name, game_id: checkList?.[1]?.value, metrics: {} };
    // 处理成统一格式
    Object.keys(compareMetrics).forEach((key) => {
      const data = compareMetrics?.[key] || {};
      top10perMetrics.metrics[key] = { value: data?.top10per || "", percentile: 0.9 };
      medianMetrics.metrics[key] = { value: data?.median || "", percentile: 0.5 };
    });
    // 需要展示的雷达图数据
    const chartDataList = [gameMetrics, ...compareGameMetrics];
    // 是否展示top10%和median
    if (activeList?.includes('top10per') && showCheck) {
      chartDataList?.push(top10perMetrics);
    }
    if (activeList?.includes('median') && showCheck) {
      chartDataList?.push(medianMetrics);
    }
    // 计算所有数据的最大值（本游戏和benchmark数据来源不用，兼容最大值处理，取所有数据指标的最大值）
    const metricsList = chartDataList?.map((item: any) => item?.metrics || {});
    // 使用提供的数据数组调用函数
    const maxMetricsList = calculateMaxMetrics([maxMetrics, ...(metricsList || [])]);
    return {
      isNodata,
      loading: chartLoading || maxLoading,
      error: chartError || maxError,
      maxMetricsList,
      chartDataList,
      currentGameId,
    };
  }, [activeList, checkList, radarChartData, radarChartMaxData, showCheck]);

  const { legendData = [], indicator = [], series = [{ name: 'radar', type: 'radar', data: [] }], metricsNameMap } = useMemo(() => {
    if (loading || isNodata) return {};
    const legendData = chartDataList?.map((item: any) => ({ name: item?.game_name || "", itemStyle: { opacity: 1 } })) || [];
    const indicator = Object.keys(maxMetricsList).map((key) => ({
      key,
      name: getLanguage(isEN, metricConfig?.[key]?.metric_en, metricConfig?.[key]?.metric_cn) || key,
      max: isPercent ? 1 : (maxMetricsList?.[key]?.value || 0),
    }));
    // 指标名称跟key的映射关系
    const metricsNameMap: any = {};
    indicator?.forEach(({ key }) => {
      metricsNameMap[metricConfig?.[key]?.metric_en || key] = key;
      metricsNameMap[metricConfig?.[key]?.metric_cn || key] = key;
    });
    const indicatorKey = indicator?.map(item => item?.key);
    // 雷达图series数据
    const seriesData = chartDataList?.map((item: any, index) => {
      // eslint-disable-next-line no-nested-ternary
      const color = item?.game_id === 'top10per' ? "#5086F3" : item?.game_id === "median" ? "#69D19C" : colorArr?.[index];
      const valueList: any = [];
      const numberValueList: any = [];
      const percentValueList: any = [];
      indicatorKey?.forEach((key) => {
        const numberValue = item?.metrics?.[key]?.value || "";
        const percentValue = item?.metrics?.[key]?.percentile || "";
        valueList?.push(isPercent ? percentValue : numberValue);
        numberValueList?.push(numberValue);
        percentValueList?.push(percentValue);
      });
      if (item?.game_id === currentGameId) {
        return {
          value: valueList,
          numberValueList,
          percentValueList,
          name: item?.game_name || "",
          areaStyle: { color, opacity: 0.5, },
          lineStyle: { color, },
          itemStyle: { color, },
        };
      }
      // 非本游戏不需要粗圆点和阴影
      return {
        value: valueList,
        numberValueList,
        percentValueList,
        name: item?.game_name || "",
        lineStyle: { color, },
        itemStyle: { color, opacity: 0 },
      };
    });

    return {
      legendData,
      indicator,
      series: [{
        name: 'radar',
        type: 'radar',
        data: seriesData
      }],
      metricsNameMap,
    };
  }, [loading, isNodata, chartDataList, maxMetricsList, isEN, metricConfig, isPercent, colorArr, currentGameId]);

  const chartEventConfig = (option: any, chart: any) => {
    if (!chart) return option;
    chart?.on('click', (params: any) => {
      if (params?.componentType === "radar") {
        const key = metricsNameMap?.[params?.name];
        const frameMap: any = {
          pc: 'pcconsole',
          mobile: 'mobile'
        };
        const frameType = frameMap?.[dataFrame || 'pc'];
        window.open(`${getBasePath()}/benchmark/${frameType}/explore/metricDetail/${key}`);
      }
    });
    return option;
  };

  const formatterFunc = (params: any) => {
    // 雷达图指标解释浮窗
    if (params?.componentType === "radar") {
      const key = metricsNameMap?.[params?.name] || "";
      const metric = metricConfig?.[key] || {};
      const tooltip = getLanguage(isEN, metric?.tooltipEn, metric?.tooltipCn);
      const dom = <div className='dbd-echarts-tooltip-container dbd-echarts-radar-name-tooltip'>{tooltip?.replaceAll('\\n', '<br/>')}</div>;
      return tooltip ? ReactDOMServer.renderToString(dom) : "";
    }

    // 雷达图游戏和top、median数据浮窗
    const dom = <div className='dbd-echarts-tooltip-container dbd-echarts-radar-tooltip'>
      <div className='dbd-echarts-radar-tooltip-content'>
        <div className='dbd-echarts-radar-tooltip-content-name'>{params?.name}</div>
      </div>
      <div className='dbd-echarts-radar-tooltip-content'>
        <div className='dbd-echarts-radar-tooltip-content-name'>{getLanguage(isEN, "Metric", "指标")}</div>
        <div className='dbd-echarts-radar-tooltip-content-value'>
          <div>{getLanguage(isEN, "Value", "指标值")}</div>
          <div>{getLanguage(isEN, "Percentile", "百分位")}</div>
        </div>
      </div>
      {
        indicator?.map((item, index) => {
          const metric = metricConfig?.[item?.key] || {};
          const value = metricValueFormatter(params?.data?.numberValueList?.[index], metric);
          const percentValue = metricValueFormatter(params?.data?.percentValueList?.[index], { formatter: 'percent1Decimal', scalar_type: 'percentage' });

          return <div className='dbd-echarts-radar-tooltip-content' key={item?.key}>
            <div className='dbd-echarts-radar-tooltip-content-name'>
              <span className='dbd-echarts-radar-tooltip-content-name-icon' style={{ backgroundColor: params?.color }}></span>
              <span>{item?.name}</span>
            </div>
            <div className='dbd-echarts-radar-tooltip-content-value'>
              <div> {value}</div>
              <div>{percentValue}</div>
            </div>
          </div>;
        })
      }
    </div>;
    return ReactDOMServer.renderToString(dom);
  };

  return <div className={cx(className)} style={style} >
    <Card className="dbd-benchmark-radar-chart register-table">
      <Card.Body>
        <div className='dbd-radar-chart-select'>
          <Select
            appearance="link"
            options={options}
            value={activeType}
            listWidth={175}
            onChange={value => activeType !== value && setActiveType(value)}
          />
        </div>
        <Abnormal
          style={{ height: height ? height : 400 }}
          component={<RadarChart
            height={height}
            legendData={legendData}
            indicator={indicator}
            series={series}
            chartEventConfig={chartEventConfig}
            formatterFunc={formatterFunc}
          />}
          isLoading={loading}
          isError={error}
          isNoData={isNodata}
        />
      </Card.Body>
    </Card>
  </div>;
};

export default RadarChartCard;